import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Save } from "lucide-react";
import {
  loyaltyProgramService,
  CreateLoyaltyProgramData,
} from "../../services/loyalty-program.service";

export default function EditLoyaltyProgramPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<Partial<CreateLoyaltyProgramData>>({
    name: "",
    description: "",
    pointsPerAmount: 1,
    amountPerPoint: 1,
  });

  const { data: program, isLoading } = useQuery({
    queryKey: ["loyalty-program", id],
    queryFn: () => loyaltyProgramService.getById(id!),
    enabled: !!id,
  });

  useEffect(() => {
    if (program) {
      setFormData({
        name: program.name,
        description: program.description,
        pointsPerAmount: program.pointsPerAmount,
        amountPerPoint: program.amountPerPoint,
        minRedeemPoints: program.minRedeemPoints,
        maxRedeemPercent: program.maxRedeemPercent,
        expiryDays: program.expiryDays,
      });
    }
  }, [program]);

  const updateMutation = useMutation({
    mutationFn: (data: Partial<CreateLoyaltyProgramData>) =>
      loyaltyProgramService.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["loyalty-programs"] });
      queryClient.invalidateQueries({ queryKey: ["loyalty-program", id] });
      navigate("/loyalty-programs");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate(formData);
  };

  const handleNumberChange = (
    key: keyof CreateLoyaltyProgramData,
    value: string,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value === "" ? undefined : Number(value),
    }));
  };

  if (isLoading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
      </div>

      <div className="max-w-3xl">
        <h1 className="text-2xl font-bold mb-6">Edit Loyalty Program</h1>

        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-lg shadow p-6 space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Program Name *
            </label>
            <input
              type="text"
              required
              value={formData.name || ""}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Points Per Amount *
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.pointsPerAmount ?? ""}
                onChange={(e) =>
                  handleNumberChange("pointsPerAmount", e.target.value)
                }
                required
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amount Per Point *
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.amountPerPoint ?? ""}
                onChange={(e) =>
                  handleNumberChange("amountPerPoint", e.target.value)
                }
                required
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Min Redeem Points
              </label>
              <input
                type="number"
                min="0"
                value={formData.minRedeemPoints ?? ""}
                onChange={(e) =>
                  handleNumberChange("minRedeemPoints", e.target.value)
                }
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Redeem Percent
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={formData.maxRedeemPercent ?? ""}
                onChange={(e) =>
                  handleNumberChange("maxRedeemPercent", e.target.value)
                }
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expiry Days
              </label>
              <input
                type="number"
                min="0"
                value={formData.expiryDays ?? ""}
                onChange={(e) =>
                  handleNumberChange("expiryDays", e.target.value)
                }
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description || ""}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              rows={3}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={updateMutation.isPending}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              {updateMutation.isPending ? "Saving..." : "Update Program"}
            </button>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>

          {updateMutation.isError && (
            <div className="text-red-600 text-sm">
              Error updating loyalty program. Please try again.
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
